import { lookupKeys } from './constants';
import {
  CatalogPrecisionFormatterConfig,
  FormattedUnitLabels,
  FormatterInput,
  isCatalogPrecisionFormatterConfig,
  NumericFormatterInput,
  UnitCatalogEntry,
  UnitFormatterType,
  UnitTransformerType,
} from './types';
import { isCompositeDisplayUnit, toNumber, unitValueToFormattedUnitLabels } from './utils';
import { createPrecisionFormatter } from '../formatters/create-precision-formatter';
import { formatDuration } from '../formatters/format-duration';
import { formatUnitless } from '../formatters/format-unitless';

const forEachLookupKey = (
  catalog: UnitCatalogEntry[],
  visit: (entry: UnitCatalogEntry, key: string) => void
): void => {
  catalog.forEach((entry) => {
    lookupKeys(entry).forEach((key) => visit(entry, key));
  });
};

const assertCatalogPrecisionConfig = (
  entry: UnitCatalogEntry,
  config: CatalogPrecisionFormatterConfig
): void => {
  const hasPrefix = Boolean(config.unitPrefix?.length);
  const hasSuffix = Boolean(config.unitSuffix?.length);
  if (!hasPrefix && !hasSuffix) {
    throw new Error(
      `Catalog entry "${entry.name}" precision formatter requires unitPrefix or unitSuffix`
    );
  }
};

const asNumericFormatter = (
  fn: (input: { value: number }) => FormattedUnitLabels
): UnitFormatterType => {
  return (input: FormatterInput): FormattedUnitLabels => {
    const numeric = toNumber(input.value);
    if (numeric == null) {
      return unitValueToFormattedUnitLabels({ value: null, units: undefined });
    }
    return fn({ value: numeric });
  };
};

/** Passthrough non-numeric strings; null/undefined → NO_VALUE. Used for numeric bespoke formatters. */
export const asPassthroughNonNumericFormatter = (
  fn: (input: NumericFormatterInput) => FormattedUnitLabels
): UnitFormatterType => {
  return (input: FormatterInput): FormattedUnitLabels => {
    const numeric = toNumber(input.value);
    if (numeric == null) {
      const isBlank = typeof input.value === 'string' && input.value.trim() === '';
      return unitValueToFormattedUnitLabels({
        value: isBlank ? null : input.value,
        units: undefined,
      });
    }
    return fn({ value: numeric });
  };
};

/** Bespoke formatters that must receive raw FormatterInput (non-numeric semantics differ from passthrough-wrap). */
export const FORMATTER_PASSTHROUGH_EXEMPT = new Set<UnitFormatterType>([
  formatDuration, // H:MM strings bypass toNumber
  formatUnitless, // owns non-numeric passthrough and unit suppression
]);

const resolveFormatter = (entry: UnitCatalogEntry): UnitFormatterType => {
  const formatter = entry.formatter;

  if (typeof formatter === 'function') {
    if (FORMATTER_PASSTHROUGH_EXEMPT.has(formatter as UnitFormatterType)) {
      return formatter as UnitFormatterType;
    }
    return asPassthroughNonNumericFormatter(
      formatter as (input: NumericFormatterInput) => FormattedUnitLabels
    );
  }

  assertCatalogPrecisionConfig(entry, formatter);
  const precisionFormatter = createPrecisionFormatter(formatter);
  return asNumericFormatter(precisionFormatter);
};

export type UnitRegistry = {
  formatters: Record<string, UnitFormatterType>;
  transformers: Record<string, UnitTransformerType>;
  /** Lookup key → catalog entry asciiSymbol (lowercased; for pivot resolution). */
  canonicalAsciiByKey: Record<string, string>;
  /** Keys whose formatter renders a value rather than labeling a unit. */
  presentationKeys: Set<string>;
  /** Presentation key → unit its formatter expects (catalog `inputUnits`). */
  presentationInputUnitsByKey: Record<string, string>;
  fallbackFormatters: {
    boolean: UnitFormatterType;
  };
};

export const buildUnitRegistry = (catalog: UnitCatalogEntry[]): UnitRegistry => {
  const formatters = Object.create(null) as Record<string, UnitFormatterType>;
  const transformers = Object.create(null) as Record<string, UnitTransformerType>;
  const canonicalAsciiByKey = Object.create(null) as Record<string, string>;
  const presentationKeys = new Set<string>();
  const presentationInputUnitsByKey = Object.create(null) as Record<string, string>;

  forEachLookupKey(catalog, (entry, key) => {
    formatters[key] = resolveFormatter(entry);
    canonicalAsciiByKey[key] = entry.asciiSymbol.toLowerCase();
    if (!isCatalogPrecisionFormatterConfig(entry.formatter) && !isCompositeDisplayUnit(key)) {
      presentationKeys.add(key);
      if (entry.inputUnits) {
        presentationInputUnitsByKey[key] = entry.inputUnits;
      }
    }
    if (entry.transform) {
      transformers[key] = entry.transform as UnitTransformerType;
    }
  });

  return {
    formatters,
    transformers,
    canonicalAsciiByKey,
    presentationKeys,
    presentationInputUnitsByKey,
    fallbackFormatters: {
      boolean: formatters.boolean,
    },
  };
};
